import { ApiNeighborContext } from '../api-types/neighbor.api';
import {
	CreateWorkplaceApiBody,
	UpdateWorkplaceApiBody,
	WorkplaceApi,
	WorkplaceJobApi,
} from '../api-types/workplace.api';
import { NeighborContext } from '../app-types/neighbors.app';
import { CreateWorkplace, UpdateWorkplaceBody, Workplace, WorkplaceJob } from '../app-types/workplace.app';
import { transformApiAddressToAppAddress, transformAppAddressToApiAddress } from './address.transform';
import { transformApiClientToAppClient, transformAppClientToApiClient } from './client.transform';
import {
	transformApiContactPointsToAppContactPoints,
	transformAppContactPointToApiCreateContactPoint,
} from './contact-point.transform';
import {
	transformApiInstitutionBranchToAppInstitutionBranch,
	transformApiInstitutionPersonsToAppInstitutionPersons,
	transformApiInstitutionPersonToAppInstitutionPerson,
	transformAppInstitutionBranchToApiInstitutionBranch,
	transformAppInstitutionPersonsToApiInstitutionPersons,
	transformAppInstitutionPersonToApiInstitutionPerson,
} from './instition.tranform';

export const transformApiWorkplaceToAppWorkplace = (input: WorkplaceApi) => {
	const output: Workplace = {
		id: input.id,
		name: input.name,
		cityDistrict: input?.cityDistrict,
		schoolNumber: input?.schoolNumber,
		occupationTitle: input.occupationTitle,
		institutionBranch: input.institutionBranch
			? transformApiInstitutionBranchToAppInstitutionBranch(input.institutionBranch)
			: undefined,
		address: input.institutionBranchAddress
			? transformApiAddressToAppAddress(input.institutionBranchAddress)
			: undefined,
		workplacesJobs: (input.workplacesJobs || []).map(transformApiWorkplaceJobToAppWorkplaceJob),
		contactPoints: transformApiContactPointsToAppContactPoints(input.contactPoints ?? []),
		industryName: input.industryName ?? '',
		institutionPersons: transformApiInstitutionPersonsToAppInstitutionPersons(input.institutionPersons ?? []),
		isMainInstitution: input.isMainInstitution,
	};

	return output;
};

/**
 * Transforms an array of ApiWorkplace objects to an array of app-specific Workplace objects.
 * @description This function is useful when you need to convert multiple Workplaces received from the API
 * into a format that the application can work with. It uses the `transformApiWorkplaceToAppWorkplace`
 * function to transform each individual Workplace.
 * @param input Array of ApiWorkplace objects.
 * @returns Array of Workplace objects.
 */
export const transformApiWorkplacesToAppWorkplaces = (input: WorkplaceApi[]) => {
	return input.map(transformApiWorkplaceToAppWorkplace);
};

/**
 * Transforms an app-specific Workplace object to an ApiWorkplace object.
 * @description This function maps properties of the Workplace to the ApiWorkplace interface.
 * It is used to convert data from the application into a format that can be sent to the API.
 * @param input Workplace
 * @returns ApiWorkplace
 */
export const transformAppWorkplaceToApiWorkplace = (input: Workplace) => {
	const output: WorkplaceApi = {
		id: input.id,
		name: input.name,
		cityDistrict: input?.cityDistrict,
		institutionBranchAddress: input.address ? transformAppAddressToApiAddress(input.address) : undefined,
		workplacesJobs: (input?.workplacesJobs ?? []).map(transformAppWorkplaceJobToApiWorkplaceJob),
		schoolNumber: input?.schoolNumber,
		industryName: input.industryName ?? '',
		contactPoints: (input.contactPoints || []).map((cp) => ({
			id: cp.id!,
			categoryContactPointId: cp.categoryContactPointId,
			contactValue: cp.contactValue,
			preferredWayOfContact: cp.preferredWayOfContact,
			country: undefined,
			remark: cp.remark,
			personId: cp.personId,
			personTypeId: cp.personTypeId,
		})),
		institutionPersons: transformAppInstitutionPersonsToApiInstitutionPersons(input.institutionPersons ?? []),
		isMainInstitution: input.isMainInstitution,
		...(input.institutionBranch
			? { institutionBranch: transformAppInstitutionBranchToApiInstitutionBranch(input.institutionBranch) }
			: {}),
	};
	return output;
};

/**
 * Transform the CreateWorkplace object to a backend-specific Workplace object
 * @param input CreateWorkplace
 * @description This function maps the properties of the CreateWorkplace to the CreateApiWorkplaceBody.
 * It is used to convert data to be sent to the API into a format that the backend can work with
 * @returns CreateApiWorkplaceBody
 */
export const transformAppCreateWorkplaceToApiCreateWorkplace = (input: CreateWorkplace) => {
	const output: CreateWorkplaceApiBody = {
		name: input.name,
		cityDistrictId: input?.residenceDistrict,
		address: input?.address,
		schoolNumber: input?.schoolNumber,
		workplaceJobs: (input?.workplacesJobs ?? []).map(transformAppWorkplaceJobToApiWorkplaceJob),
		contactPoints: input.contactPoints.map(transformAppContactPointToApiCreateContactPoint ?? []),
		industryName: input.industryName ?? '',
	};
	return output;
};

export const transformApiWorkplaceJobToAppWorkplaceJob = (input: WorkplaceJobApi): WorkplaceJob => {
	const output: WorkplaceJob = {
		id: input.id,
		typeAndScopeOfTheWorkplace: input.typeAndScopeOfTheWorkplace,
		remark: input.remark,
		institutionInformed: input.institutionInformed,
		occupationTitle: input.occupationTitle,
		isActive: input.isActive,
		institutionBranch: input.institutionBranch
			? transformApiInstitutionBranchToAppInstitutionBranch(input.institutionBranch)
			: undefined,
		institutionPerson: input.institutionPerson
			? transformApiInstitutionPersonToAppInstitutionPerson(input.institutionPerson)
			: undefined,
		client: input.client ? transformApiClientToAppClient(input.client) : undefined,
		createdAt: new Date(input.createdAt),
	};
	return output;
};

export const transformAppWorkplaceJobToApiWorkplaceJob = (input: WorkplaceJob): WorkplaceJobApi => {
	const output: WorkplaceJobApi = {
		id: input.id,
		typeAndScopeOfTheWorkplace: input.typeAndScopeOfTheWorkplace,
		remark: input.remark,
		institutionInformed: input.institutionInformed,
		occupationTitle: input.occupationTitle,
		isActive: input.isActive,
		institutionBranch: input.institutionBranch
			? transformAppInstitutionBranchToApiInstitutionBranch(input.institutionBranch)
			: undefined,
		institutionPerson: input.institutionPerson
			? transformAppInstitutionPersonToApiInstitutionPerson(input.institutionPerson)
			: undefined,
		client: input.client ? transformAppClientToApiClient(input.client) : undefined,
		createdAt: input.createdAt.toISOString(),
	};
	return output;
};

export const transformAppWorkplaceJobsToApiWorkplaceJobs = (input: WorkplaceJob[]) => {
	const output: WorkplaceJobApi[] = input.map(transformAppWorkplaceJobToApiWorkplaceJob);
	return output;
};

export const transformAppUpdateWorkplaceBodyToApiUpdateWorkplaceBody = (input: UpdateWorkplaceBody) => {
	const output: UpdateWorkplaceApiBody = {
		name: input.name,
		address: input.address,
		cityDistrictId: input.cityDistrictId,
		industryName: input.industryName,
		contactPoints: input.contactPoints,
		schoolNumber: input.schoolNumber,
		workplaceJobs: transformAppWorkplaceJobsToApiWorkplaceJobs(input.workplaceJobs ?? []),
	};
	return output;
};

export function transformApiInstitutionWorkplaceNeighborsToAppInstitutionWorkplaceNeighbors(
	input: ApiNeighborContext<WorkplaceApi>,
): NeighborContext<Workplace> {
	return {
		current: transformApiWorkplaceToAppWorkplace(input.current),
		previous: input.previous ? transformApiWorkplaceToAppWorkplace(input.previous) : null,
		next: input.next ? transformApiWorkplaceToAppWorkplace(input.next) : null,
	};
}
